import { create } from 'zustand';

import { apiFetch } from '@/lib/api';
import { endSession, readSession, writeSession, type Session, type SessionUser } from '@/lib/session';

import { disableGoogleAutoSelect } from './google';

type AuthResponse = {
  accessToken: string;
  expiresAt: string;
  user: SessionUser;
  isNewUser: boolean;
};

type AuthState = {
  session: Session | null;
  /** Exchanges a Google ID token for an API session. Resolves to true for a brand-new account. */
  signInWithGoogle: (credential: string) => Promise<boolean>;
  signOut: () => void;
};

export const useAuthStore = create<AuthState>((set) => ({
  session: readSession(),

  signInWithGoogle: async (credential) => {
    const response = await apiFetch<AuthResponse>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    });

    const session: Session = {
      accessToken: response.accessToken,
      expiresAt: response.expiresAt,
      user: response.user,
    };
    writeSession(session);
    set({ session });
    return response.isNewUser;
  },

  signOut: () => {
    disableGoogleAutoSelect();
    endSession();
  },
}));

export function useCurrentUser(): SessionUser | null {
  return useAuthStore((state) => state.session?.user ?? null);
}
